import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Camera,
  Minus,
  Plus,
  ChevronRight,
  X,
  Check,
  CircleAlert,
} from "lucide-react";
import RkAccountMenu from "../../components/RkAccountMenu/RkAccountMenu";
import {
  getRkCategories,
  markRkReturn,
  searchRkProducts,
  uploadRkReturnImage,
} from "../../network/externalApi";
import { formatLongDate } from "utils/dateFormat";
import OrderImageCapture from "../../components/OrderImageCapture/OrderImageCapture";
import styles from "./MarkOrderReturn.module.scss";

const SEARCH_DEBOUNCE_MS = 300;
const LOAD_MORE_THRESHOLD_PX = 80;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
// Set to true when item-level return images become mandatory.
const REQUIRE_RETURN_ITEM_IMAGES = false;
const REQUIRE_RETURN_ORDER_IMAGES = false;
const ALL_CATEGORY = { id: "all", name: "All" };

const asArray = (v) => (Array.isArray(v) ? v : []);

const extractSubcategories = (raw) => {
  return asArray(raw?.data?.response?.Categories)
    .map((category) => ({
      id: category?.Id,
      parent_id:category?.ParentId,
      name: category?.Name,
    }))
    .filter((category) => category.id != null && category.name);
};

const pickProductId = (p) => p?.ProductNumber;
const pickProductName = (p) => p?.Name || "";
const extractProducts = (raw) =>
  asArray(
      raw?.data?.response?.Products ||
      []
  );

const toNonNegativeNumber = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
};

const extractPagination = (raw, productsCount) => {
  const response = raw?.data?.response || raw?.data || {};
  const pagination = response?.Pagination || response?.pagination || response;
  const offset = toNonNegativeNumber(
    pagination?.Offset,
    0
  );
  const count = toNonNegativeNumber(
    pagination?.Count,
    productsCount
  );
  const total = toNonNegativeNumber(
    pagination?.Total,
    offset + productsCount
  );

  return {
    hasMore: productsCount > 0 && offset + count < total,
  };
};

const extractUploadedImagePath = (raw) => {
  const response = raw?.data?.response;
  if (typeof response === "string") return response;
  return (
    response?.ImageUrl ||
    ""
  );
};

const extractReturnCode = (raw) => {
  const response = raw?.data?.response;
  if (typeof response === "string" || typeof response === "number") {
    return String(response);
  }
  return response?.LabelCode == null ? "" : String(response.LabelCode);
};

const extractErrorMessage = (error) => {
  const responseError = error?.response?.data?.error;
  return (
    error?.response?.data?.message ||
    (typeof responseError === "string"
      ? responseError
      : responseError?.message) ||
    error?.message ||
    "Unable to mark this return. Please try again."
  );
};

const toApiIdentifier = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : value;
};

const MarkOrderReturn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { outletId: paramOutletId, orderId: paramOrderId } = useParams();
  const outletId = paramOutletId || location.state?.outletId;
  const orderId = paramOrderId || location.state?.orderId;

  const outletName = location.state?.outletName || "Outlet";
  const orderDate = location.state?.orderDate || "";

  const [keyword, setKeyword] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [categoriesLoadedForOutlet, setCategoriesLoadedForOutlet] =
    useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(ALL_CATEGORY.id);
  const [products, setProducts] = useState([]);
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");
  const [quantities, setQuantities] = useState({});
  const [productImages, setProductImages] = useState({});
  const [imageUploads, setImageUploads] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [orderImages, setOrderImages] = useState([]);
  const [isOrderImageUploading, setIsOrderImageUploading] = useState(false);
  const [orderImageError, setOrderImageError] = useState("");
  const [showOrderImageCapture, setShowOrderImageCapture] = useState(false);
  const [returnDialog, setReturnDialog] = useState(null);
  const [cartonCount, setCartonCount] = useState("");
  const [cartonError, setCartonError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [returnResult, setReturnResult] = useState(null);
  const requestVersionRef = useRef(0);
  const nextPageRef = useRef(2);
  const isLoadingMoreRef = useRef(false);
  const productImagesRef = useRef(productImages);
  const orderImagesRef = useRef(orderImages);

  useEffect(() => {
    productImagesRef.current = productImages;
  }, [productImages]);

  useEffect(() => {
    orderImagesRef.current = orderImages;
  }, [orderImages]);

  useEffect(
    () => () => {
      Object.values(productImagesRef.current)
        .flat()
        .forEach((image) => URL.revokeObjectURL(image.previewUrl));
      orderImagesRef.current.forEach((image) =>
        URL.revokeObjectURL(image.previewUrl)
      );
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    setSubcategories([]);
    setCategoriesLoadedForOutlet(null);
    (async () => {
      try {
        const resp = await getRkCategories({ outletId });
        if (cancelled) return;
        setSubcategories(extractSubcategories(resp));
      } catch {
        if (!cancelled) setSubcategories([]);
      } finally {
        if (!cancelled) setCategoriesLoadedForOutlet(outletId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [outletId]);

  const allCategoryIdsCsv = useMemo(
    () => {
      let categories = subcategories
          .map((c) => c?.parent_id)
          .filter((id) => id != null)
      let uniqueCategories = [...new Set(categories)];
      return uniqueCategories.join(",")
    },
    [subcategories]
  );

  useEffect(() => {
    if (outletId == null || categoriesLoadedForOutlet !== outletId) {
      return undefined;
    }

    let cancelled = false;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    const trimmed = keyword.trim();
    const catForApi =
      selectedCategoryId === ALL_CATEGORY.id
        ? allCategoryIdsCsv
        : selectedCategoryId;

    nextPageRef.current = 2;
    isLoadingMoreRef.current = false;
    setProducts([]);
    setHasMoreProducts(false);
    setIsLoadingMore(false);
    setLoadMoreError("");

    const t = setTimeout(async () => {
      setIsProductsLoading(true);
      setProductsError("");
      try {
        const resp = await searchRkProducts({
          outletId,
          categoryIds: catForApi || undefined,
          keyword: trimmed || undefined,
          pageNumber: 1,
        });
        if (cancelled || requestVersion !== requestVersionRef.current) return;
        const nextProducts = extractProducts(resp);
        setProducts(nextProducts);
        setHasMoreProducts(
          extractPagination(resp, nextProducts.length).hasMore
        );
      } catch (err) {
        if (cancelled || requestVersion !== requestVersionRef.current) return;
        setProductsError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load products"
        );
        setProducts([]);
      } finally {
        if (!cancelled && requestVersion === requestVersionRef.current) {
          setIsProductsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    outletId,
    keyword,
    selectedCategoryId,
    allCategoryIdsCsv,
    categoriesLoadedForOutlet,
  ]);

  const loadMoreProducts = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreProducts) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    setLoadMoreError("");

    const requestVersion = requestVersionRef.current;
    const pageNumber = nextPageRef.current;
    const trimmed = keyword.trim();
    const catForApi =
      selectedCategoryId === ALL_CATEGORY.id
        ? allCategoryIdsCsv
        : selectedCategoryId;

    try {
      const resp = await searchRkProducts({
        outletId,
        categoryIds: catForApi || undefined,
        keyword: trimmed || undefined,
        pageNumber,
      });
      if (requestVersion !== requestVersionRef.current) return;

      const nextProducts = extractProducts(resp);
      const pagination = extractPagination(resp, nextProducts.length);
      setProducts((current) => [...current, ...nextProducts]);
      setHasMoreProducts(pagination.hasMore);
      nextPageRef.current = pageNumber + 1;
    } catch (err) {
      if (requestVersion !== requestVersionRef.current) return;
      setLoadMoreError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load more products"
      );
    } finally {
      if (requestVersion === requestVersionRef.current) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [
    allCategoryIdsCsv,
    hasMoreProducts,
    keyword,
    outletId,
    selectedCategoryId,
  ]);

  const handleProductsScroll = useCallback(
    (event) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom <= LOAD_MORE_THRESHOLD_PX) {
        loadMoreProducts();
      }
    },
    [loadMoreProducts]
  );

  const clearProductImages = (id) => {
    setProductImages((current) => {
      asArray(current[id]).forEach((image) =>
        URL.revokeObjectURL(image.previewUrl)
      );
      const { [id]: _removed, ...rest } = current;
      return rest;
    });
    setImageErrors((current) => {
      const { [id]: _removed, ...rest } = current;
      return rest;
    });
  };

  const setQty = (id, next) => {
    const safe = Math.max(0, Number.isFinite(next) ? next : 0);
    setQuantities((prev) => {
      if (safe === 0) {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: safe };
    });

    if (safe === 0) {
      clearProductImages(id);
    }
  };

  const handleImageSelection = async (id, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const invalidFile = files.find(
      (file) =>
        !file.type.startsWith("image/") || file.size > MAX_IMAGE_SIZE_BYTES
    );
    if (invalidFile) {
      setImageErrors((current) => ({
        ...current,
        [id]: "Upload an image smaller than 10 MB",
      }));
      return;
    }

    setImageUploads((current) => ({ ...current, [id]: true }));
    setImageErrors((current) => ({ ...current, [id]: "" }));

    const uploadedImages = [];
    try {
      for (const file of files) {
        const response = await uploadRkReturnImage({ file });
        const path = extractUploadedImagePath(response);
        if (!path) throw new Error("Image upload failed");
        uploadedImages.push({
          id: `${Date.now()}-${Math.random()}`,
          path,
          previewUrl: URL.createObjectURL(file),
        });
      }

      setProductImages((current) => ({
        ...current,
        [id]: [...asArray(current[id]), ...uploadedImages],
      }));
    } catch (error) {
      uploadedImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      setImageErrors((current) => ({
        ...current,
        [id]:
          error?.response?.data?.message ||
          error?.message ||
          "Image upload failed",
      }));
    } finally {
      setImageUploads((current) => ({ ...current, [id]: false }));
    }
  };

  const removeProductImage = (productId, imageId) => {
    setProductImages((current) => {
      const image = asArray(current[productId]).find(
        (item) => item.id === imageId
      );
      if (image) URL.revokeObjectURL(image.previewUrl);
      return {
        ...current,
        [productId]: asArray(current[productId]).filter(
          (item) => item.id !== imageId
        ),
      };
    });
  };

  const handleOrderImageSelection = async (files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;

    const invalidFile = selectedFiles.find(
      (file) =>
        !file.type.startsWith("image/") || file.size > MAX_IMAGE_SIZE_BYTES
    );
    if (invalidFile) {
      setOrderImageError("Upload images smaller than 10 MB");
      return;
    }

    setIsOrderImageUploading(true);
    setOrderImageError("");

    const uploadedImages = [];
    try {
      for (const file of selectedFiles) {
        const response = await uploadRkReturnImage({ file });
        const path = extractUploadedImagePath(response);
        if (!path) throw new Error("Image upload failed");
        uploadedImages.push({
          id: `${Date.now()}-${Math.random()}`,
          path,
          previewUrl: URL.createObjectURL(file),
        });
      }

      setOrderImages((current) => [...current, ...uploadedImages]);
    } catch (error) {
      uploadedImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      setOrderImageError(
        error?.response?.data?.message ||
          error?.message ||
          "Image upload failed"
      );
    } finally {
      setIsOrderImageUploading(false);
    }
  };

  const removeOrderImage = (imageId) => {
    setOrderImages((current) => {
      const image = current.find((item) => item.id === imageId);
      if (image) URL.revokeObjectURL(image.previewUrl);
      return current.filter((item) => item.id !== imageId);
    });
  };

  const totals = useMemo(() => {
    const units = Object.values(quantities).reduce((a, b) => a + b, 0);
    const items = Object.keys(quantities).length;
    return { units, items };
  }, [quantities]);

  const canSubmit =
    orderId != null &&
    totals.units > 0 &&
    !isOrderImageUploading &&
    (!REQUIRE_RETURN_ORDER_IMAGES || orderImages.length > 0) &&
    (!REQUIRE_RETURN_ITEM_IMAGES ||
      Object.keys(quantities).every(
        (id) => asArray(productImages[id]).length > 0 && !imageUploads[id]
      ));

  const closeCartonDialog = () => {
    if (isSubmitting) return;
    setReturnDialog(null);
    setCartonError("");
  };

  const handleMarkReturn = () => {
    if (!canSubmit) return;
    setCartonError("");
    setReturnDialog("cartons");
  };

  const handleReturnSubmission = async () => {
    const numberOfCartons = Number(cartonCount);
    if (
      !/^\d+$/.test(cartonCount) ||
      !Number.isSafeInteger(numberOfCartons) ||
      numberOfCartons < 1
    ) {
      setCartonError("Enter a valid number of cartons");
      return;
    }

    setCartonError("");
    setIsSubmitting(true);

    const returnItems = Object.entries(quantities).map(
      ([productNumber, returnedCount]) => ({
        ProductNumber: toApiIdentifier(productNumber),
        ReturnedCount: returnedCount,
        ImageUrls: asArray(productImages[productNumber]).map(
          (image) => image.path
        ),
      })
    );

    try {
      const response = await markRkReturn({
        OrderId: toApiIdentifier(orderId),
        Items: returnItems,
        NumberOfCartons: numberOfCartons,
        ImageUrls: orderImages.map((image) => image.path),
      });
      setReturnResult({
        type: "success",
        code: extractReturnCode(response),
      });
      setReturnDialog("result");
    } catch (error) {
      setReturnResult({
        type: "error",
        message: extractErrorMessage(error),
      });
      setReturnDialog("result");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultAcknowledgement = () => {
    if (returnResult?.type === "success") {
      navigate("/rk/home", { replace: true });
      return;
    }

    setReturnResult(null);
    setReturnDialog("cartons");
  };

  const categoryChips = [
    ALL_CATEGORY,
    ...subcategories.map((c) => ({ id: c?.id, name: c?.name })),
  ].filter((c) => c.id != null && c.name);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className={styles.headerText}>
          <div className={styles.title}>{outletName}</div>
          {orderDate ? (
            <div className={styles.subtitle}>{formatLongDate(orderDate)}</div>
          ) : null}
        </div>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.orderImageButton}`}
          onClick={() => setShowOrderImageCapture(true)}
          aria-label={`Capture order images${
            orderImages.length ? `, ${orderImages.length} added` : ""
          }`}
        >
          <Camera size={18} />
          {orderImages.length > 0 ? (
            <span className={styles.imageCountBadge} aria-hidden="true">
              {orderImages.length > 99 ? "99+" : orderImages.length}
            </span>
          ) : null}
        </button>
        <RkAccountMenu buttonClassName={styles.iconButton} />
      </div>

      <div className={styles.content}>
        <div className={styles.searchRow}>
          <span className={styles.searchIcon}>
            <Search size={18} />
          </span>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by item or SKU number"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>

        <div className={styles.chipRow}>
          {categoryChips.map((c) => {
            const active = String(c.id) === String(selectedCategoryId);
            return (
              <button
                key={c.id}
                type="button"
                className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                onClick={() => setSelectedCategoryId(c.id)}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        <div
          className={styles.productList}
          onScroll={handleProductsScroll}
          aria-busy={isProductsLoading || isLoadingMore}
        >
          {isProductsLoading ? (
            <div className={styles.stateText}>Loading products…</div>
          ) : productsError ? (
            <div className={`${styles.stateText} ${styles.errorText}`}>
              {productsError}
            </div>
          ) : products.length === 0 ? (
            <div className={styles.stateText}>No products found</div>
          ) : (
            products.map((p, idx) => {
              const id = pickProductId(p) ?? idx;
              const name = pickProductName(p) || "Product";
              const qty = quantities[id] || 0;
              const images = asArray(productImages[id]);
              const isUploadingImage = Boolean(imageUploads[id]);
              const showImageSection = REQUIRE_RETURN_ITEM_IMAGES && qty > 0;
              return (
                <div key={id} className={styles.productCard}>
                  <div className={styles.productMainRow}>
                    <div className={styles.productName}>{name}</div>
                    <div className={styles.qtyBox}>
                      <button
                        type="button"
                        className={styles.qtyBtn}
                        onClick={() => setQty(id, qty - 1)}
                        aria-label="Decrease"
                        disabled={qty === 0 || isUploadingImage}
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={styles.qtyValue}
                        value={qty}
                        disabled={isUploadingImage}
                        onFocus={(event) => event.target.select()}
                        onChange={(event) => {
                          const digits = event.target.value.replace(/\D/g, "");
                          setQty(id, digits === "" ? 0 : parseInt(digits, 10));
                        }}
                        aria-label="Quantity"
                      />
                      <button
                        type="button"
                        className={styles.qtyBtn}
                        onClick={() => setQty(id, qty + 1)}
                        aria-label="Increase"
                        disabled={isUploadingImage}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>

                  {showImageSection ? (
                    <div className={styles.imageSection}>
                      <div className={styles.imageLabel}>
                        Image required <span aria-hidden="true">*</span>
                      </div>
                      <div className={styles.imageRow}>
                        <label
                          className={`${styles.imageUploadTile} ${
                            isUploadingImage
                              ? styles.imageUploadTileDisabled
                              : ""
                          }`}
                          aria-label="Add product image"
                        >
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            disabled={isUploadingImage}
                            onChange={(event) =>
                              handleImageSelection(id, event)
                            }
                          />
                          <span className={styles.cameraIcon}>
                            <Camera size={24} />
                            <Plus size={12} />
                          </span>
                          {isUploadingImage ? (
                            <span className={styles.uploadingText}>
                              Uploading…
                            </span>
                          ) : null}
                        </label>

                        {images.map((image) => (
                          <div key={image.id} className={styles.imagePreview}>
                            <img src={image.previewUrl} alt="Return item" />
                            <button
                              type="button"
                              className={styles.removeImageButton}
                              onClick={() => removeProductImage(id, image.id)}
                              aria-label="Remove image"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                      {imageErrors[id] ? (
                        <div className={styles.imageError}>
                          {imageErrors[id]}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
          {isLoadingMore ? (
            <div className={styles.paginationText}>Loading more products…</div>
          ) : null}
          {loadMoreError ? (
            <button
              type="button"
              className={styles.retryButton}
              onClick={loadMoreProducts}
            >
              {loadMoreError}. Tap to retry
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.bottomSummary}>
          <div className={styles.bottomLabel}>Marked for return</div>
          <div className={styles.bottomCounts}>
            {totals.units} units · {totals.items} items
          </div>
        </div>
        <button
          type="button"
          className={styles.submitBtn}
          disabled={!canSubmit}
          onClick={handleMarkReturn}
        >
          Mark return
          <ChevronRight size={18} />
        </button>
      </div>

      {returnDialog === "cartons" ? (
        <div className={styles.modalBackdrop} role="presentation">
          <div
            className={styles.cartonDialogStack}
            role="dialog"
            aria-modal="true"
            aria-labelledby="carton-dialog-title"
          >
            <button
              type="button"
              className={styles.dialogCloseButton}
              onClick={closeCartonDialog}
              disabled={isSubmitting}
              aria-label="Close"
            >
              <X size={22} />
            </button>
            <div className={styles.cartonSheet}>
              <div className={styles.alertIcon} aria-hidden="true">
                <CircleAlert size={32} />
              </div>
              <h2 id="carton-dialog-title" className={styles.dialogTitle}>
                Enter number of cartons in which return is packed
              </h2>
              <label className={styles.cartonInputLabel}>
                <span className={styles.srOnly}>Number of cartons</span>
                <input
                  className={`${styles.cartonInput} ${
                    cartonError ? styles.cartonInputError : ""
                  }`}
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  placeholder="Enter number of cartons"
                  value={cartonCount}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    setCartonCount(event.target.value);
                    setCartonError("");
                  }}
                />
              </label>
              {cartonError ? (
                <div className={styles.cartonError}>{cartonError}</div>
              ) : null}
              <div className={styles.cartonActions}>
                <button
                  type="button"
                  className={styles.goBackButton}
                  onClick={closeCartonDialog}
                  disabled={isSubmitting}
                >
                  Go back
                </button>
                <button
                  type="button"
                  className={styles.proceedButton}
                  onClick={handleReturnSubmission}
                  disabled={!cartonCount || isSubmitting}
                >
                  {isSubmitting ? "Submitting…" : "Proceed"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {returnDialog === "result" && returnResult ? (
        <div className={styles.modalBackdrop} role="presentation">
          <div
            className={styles.resultDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="return-result-title"
          >
            <div
              className={`${styles.resultIcon} ${
                returnResult.type === "success"
                  ? styles.resultIconSuccess
                  : styles.resultIconError
              }`}
              aria-hidden="true"
            >
              {returnResult.type === "success" ? (
                <Check size={30} strokeWidth={3} />
              ) : (
                <X size={30} strokeWidth={3} />
              )}
            </div>
            <h2 id="return-result-title" className={styles.resultTitle}>
              {returnResult.type === "success"
                ? returnResult.code
                  ? "Please write this code on each carton of this return"
                  : "Return marked successfully"
                : returnResult.message}
            </h2>
            {returnResult.type === "success" && returnResult.code ? (
              <div className={styles.returnCode}>{returnResult.code}</div>
            ) : null}
            <button
              type="button"
              className={styles.resultButton}
              onClick={handleResultAcknowledgement}
            >
              Okay
            </button>
          </div>
        </div>
      ) : null}

      {showOrderImageCapture ? (
        <OrderImageCapture
          images={orderImages}
          isUploading={isOrderImageUploading}
          uploadError={orderImageError}
          onAddFiles={handleOrderImageSelection}
          onRemoveImage={removeOrderImage}
          onClose={() => setShowOrderImageCapture(false)}
        />
      ) : null}
    </div>
  );
};

export default MarkOrderReturn;
